import { inject, Injectable } from '@angular/core';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { ImpersonationService } from '@core/services/impersonation.service';
import { isOnAnonymousAccessiblePage } from '@core/utils/app-initializer-utilities/anonymous-pages';
import { didPurgeLegacyAzureSession } from '@core/utils/app-initializer-utilities/legacy-azure-storage';
import { currentReturnUrl, ReturnUrlParameter } from '@core/utils/return-url.utilities';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
  LoggerService,
} from '@kerberos-compliance/compliance-shared-ui';
import { AuthFacade } from '@/auth/facades/auth.facade';
import { InactivityService } from '@/auth/services/inactivity.service';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';

/**
 * Coordinates the asynchronous application bootstrap sequence triggered via `provideAppInitializer`.
 *
 * Hanko is the only identity provider, and it authenticates in-app, so the
 * bootstrap never has to process an external redirect callback: a visitor either
 * arrives with an impersonation context, with a restorable session (the ADP
 * platform token rehydrated from storage), or anonymously — in which case they
 * are sent to the login page.
 */
@Injectable({
  providedIn: 'root',
})
export class AppInitializer {
  private isInitializing = false;

  private readonly authFacade = inject(AuthFacade);

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly navigationService = inject(NavigationService);

  private readonly impersonationService = inject(ImpersonationService);

  private readonly inactivityService = inject(InactivityService);

  private readonly logger = inject(LoggerService);

  private readonly asyncErrorHandler = createAsyncErrorHandler();

  /**
   * Performs the initialization process for the application: resolves an
   * impersonation context from the URL if present, otherwise restores the
   * existing session or routes an anonymous visitor to the login page.
   *
   * Concurrent invocations are ignored so a second bootstrap cannot race the first.
   *
   * @returns {Promise<void>} A promise that resolves when the initialization is complete.
   */
  public async initialize(): Promise<void> {
    if (this.isInitializing) {
      this.logger.warn('[AppInitializer] App initialization already in progress.');
      return;
    }

    this.isInitializing = true;
    this.logger.debug('[AppInitializer] Starting app initialization...');

    try {
      // Runs before anything else so a session left behind by the removed Azure
      // login cannot be mistaken for a restorable one. The store has already
      // hydrated from storage at this point, so the in-memory copy goes too —
      // the whole session, not just the tokens: user and grants hydrate
      // independently, and leaving them behind kept the app "logged in" against a
      // session it had no token for (AML-DESK-AW).
      if (didPurgeLegacyAzureSession(this.logger)) {
        this.authFacade.resetSessionState();
      }

      await this.handleImpersonationFlow();

      if (this.impersonationService.shouldBypassExternalAuth()) {
        this.logger.debug('[AppInitializer] Impersonation context detected or initialized.');
        this.inactivityService.startTracking();
        return;
      }

      await this.restoreSessionOrGoToLogin();
    } catch (error) {
      this.handleInitializationError(error);
    } finally {
      this.isInitializing = false;
      this.logger.debug('[AppInitializer] App initialization finished.');
    }
  }

  /**
   * Handles the impersonation flow by initializing the impersonation service with data from the URL.
   *
   * @returns {Promise<void>} A promise that resolves once the impersonation flow has been handled.
   */
  private async handleImpersonationFlow(): Promise<void> {
    this.logger.debug('[AppInitializer] handleImpersonationFlow: attempting impersonation bootstrap.');
    const isInitialized = await this.impersonationService.initializeFromUrl();
    this.logger.debug(`[AppInitializer] handleImpersonationFlow: completed with result=${isInitialized}.`);
  }

  /**
   * Re-establishes an existing session, or sends an anonymous visitor to the login page.
   *
   * The post-login hard reload wipes in-memory session state, so grants →
   * active organization → contract query are re-established here; otherwise
   * contract-gated routes (dashboard, risk analysis) hang because
   * `ContractStore.query` is never restored. Both loads are fire-and-forget —
   * the guards react to the signals once the requests resolve.
   *
   * @returns {Promise<void>} Resolves once the session was restored or the redirect was issued.
   */
  private async restoreSessionOrGoToLogin(): Promise<void> {
    const platformToken = this.authFacade.authTokens().accessTokenKerberos;

    if (platformToken) {
      this.logger.debug('[AppInitializer] Existing session detected, restoring grants and profile.');
      void this.authFacade.loadGrants(platformToken);
      void this.userProfileFacade.loadUserProfile();
      this.inactivityService.startTracking();
      return;
    }

    // Pages that are meant to be viewed without a session (the login page, the
    // logout confirmation and the error pages) must keep their URL: re-navigating
    // would drop the query string — e.g. the `?email=` deep link carried by an
    // invitation — and would hide the reason the user was sent there (KCP-1140).
    if (isOnAnonymousAccessiblePage()) {
      this.logger.debug('[AppInitializer] No session, but on an anonymously accessible page. Preserving URL.');
      return;
    }

    const returnUrl = currentReturnUrl();
    this.logger.debug('[AppInitializer] No session detected. Redirecting to the login page.');
    await this.navigationService.navigateTo(
      'login',
      undefined,
      returnUrl ? { extras: { queryParams: { [ReturnUrlParameter]: returnUrl } } } : undefined,
    );
  }

  /**
   * Handles critical errors that occur during the application initialization process.
   *
   * Logs the error and delegates error handling to the `ErrorHandlerService` with the appropriate metadata.
   * This ensures that severe initialization-related issues are processed while allowing the application
   * to potentially render a fallback UI instead of crashing.
   *
   * @param {unknown} error The error that occurred during the application initialization.
   * @returns {void} This method does not return a value.
   */
  private handleInitializationError(error: unknown): void {
    this.logger.warn('[AppInitializer] Critical error during app initialization:');
    void this.asyncErrorHandler(
      async () => {
        throw error;
      },
      {
        metadata: createErrorMetadata('AppInitializer', 'Application Bootstrap', ErrorSeverityMap.High),
        rethrow: false, // Do not re-throw, as it might prevent the app from rendering a fallback UI
      },
    );
  }
}
